import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo, useCallback } from 'react';
import * as filestack from 'filestack-js';
/**
 * Generate a unique ID for tracking files
 */
function generateId() {
    return `file-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
/**
 * Email validation regex
 */
const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
/**
 * CaptionConverter component - converts SRT files to various caption formats
 */
export function CaptionConverter({ filestackConfig, exportTypes, apiBaseUrl, }) {
    // State
    const [files, setFiles] = useState([]);
    const [selectedExportTypes, setSelectedExportTypes] = useState(new Set());
    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState([]);
    const [converterState, setConverterState] = useState('wizard');
    // Computed values
    const hasFiles = files.length > 0;
    const hasSelectedExportTypes = selectedExportTypes.size > 0;
    const hasInvalidFiles = files.some((f) => f.isValid === false);
    const allFilesValidated = files.every((f) => f.isValid !== undefined);
    const currentStep = useMemo(() => {
        if (!hasFiles)
            return 1;
        if (!hasSelectedExportTypes)
            return 2;
        return 3;
    }, [hasFiles, hasSelectedExportTypes]);
    /**
     * Color of the connector line that follows a given step:
     * - both this step and the next complete -> solid purple
     * - this step complete, next incomplete   -> purple-to-gray gradient
     * - neither complete                       -> solid gray
     */
    const connectorClass = (stepNum) => {
        if (currentStep > stepNum)
            return 'connector-purple';
        if (currentStep === stepNum)
            return 'connector-gradient';
        return 'connector-gray';
    };
    /**
     * Validate an uploaded file by calling the backend API
     */
    const validateFile = useCallback(async (file) => {
        try {
            const response = await fetch(`${apiBaseUrl}/captionconverter/checkvalidsrt?key=${encodeURIComponent(file.awsKey)}`, {
                credentials: 'include',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            if (response.ok) {
                setFiles((prev) => prev.map((f) => f.id === file.id ? { ...f, isValid: true } : f));
            }
            else {
                const errorText = await response.text();
                setFiles((prev) => prev.map((f) => f.id === file.id
                    ? { ...f, isValid: false, errorMessage: errorText || 'Invalid SRT file' }
                    : f));
            }
        }
        catch (error) {
            console.error('Error validating file:', error);
            setFiles((prev) => prev.map((f) => f.id === file.id
                ? { ...f, isValid: false, errorMessage: 'Failed to validate file' }
                : f));
        }
    }, [apiBaseUrl]);
    /**
     * Open Filestack picker for SRT file upload
     */
    const handleUploadClick = useCallback(() => {
        const client = filestack.init(filestackConfig.apiKey, {
            security: filestackConfig.policy && filestackConfig.signature
                ? { policy: filestackConfig.policy, signature: filestackConfig.signature }
                : undefined,
        });
        const pickerOptions = {
            fromSources: ['local_file_system', 'googledrive', 'dropbox', 'box', 'onedrive'],
            accept: ['.srt'],
            maxFiles: 25,
            uploadInBackground: false,
            storeTo: {
                container: filestackConfig.uploadBucket,
                path: filestackConfig.uploadPath,
                region: filestackConfig.region,
            },
            onUploadDone: (result) => {
                if (result.filesUploaded && result.filesUploaded.length > 0) {
                    const newFiles = [];
                    for (const uploaded of result.filesUploaded) {
                        if (!uploaded.key) {
                            continue;
                        }
                        newFiles.push({
                            id: generateId(),
                            name: uploaded.filename,
                            awsKey: uploaded.key,
                            isValid: undefined,
                        });
                    }
                    if (newFiles.length > 0) {
                        setFiles((prev) => [...prev, ...newFiles]);
                        // Validate each uploaded file
                        newFiles.forEach((file) => validateFile(file));
                    }
                    if (newFiles.length !== result.filesUploaded.length) {
                        setErrors((prev) => [
                            ...prev,
                            'Some uploaded files were missing storage keys. Please try again.',
                        ]);
                    }
                }
                if (result.filesFailed && result.filesFailed.length > 0) {
                    setErrors((prev) => [
                        ...prev,
                        'Some files failed to upload. Please try again.',
                    ]);
                }
            },
        };
        client.picker(pickerOptions).open();
        // Track analytics if available
        if (typeof window !== 'undefined') {
            window.analytics?.track('Button Click', {
                altEventName: 'CaptionConverter: Upload Caption File',
                stateChange: 'Filestack Modal Open',
                locationUrl: window.location.href,
                buttonText: 'Select SRT file',
                buttonLocation: 'Main Page',
            });
        }
    }, [filestackConfig, validateFile]);
    /**
     * Remove a file from the list
     */
    const handleRemoveFile = useCallback((fileId) => {
        setFiles((prev) => prev.filter((f) => f.id !== fileId));
        // Clear errors if no more invalid files
        setErrors([]);
    }, []);
    /**
     * Toggle export type selection
     */
    const handleToggleExportType = useCallback((typeName) => {
        setSelectedExportTypes((prev) => {
            const next = new Set(prev);
            if (next.has(typeName)) {
                next.delete(typeName);
            }
            else {
                next.add(typeName);
            }
            return next;
        });
    }, []);
    /**
     * Validate form before conversion
     */
    const validate = useCallback(() => {
        const validationErrors = [];
        if (!hasFiles) {
            validationErrors.push('Please upload at least one .srt file');
        }
        if (!hasSelectedExportTypes) {
            validationErrors.push('Please choose at least one export type');
        }
        if (hasInvalidFiles) {
            validationErrors.push('At least one file is not a valid .srt');
        }
        if (!EMAIL_REGEX.test(email)) {
            validationErrors.push('Please input a valid email address');
        }
        setErrors(validationErrors);
        return validationErrors.length === 0;
    }, [hasFiles, hasSelectedExportTypes, hasInvalidFiles, email]);
    /**
     * Submit conversion request
     */
    const handleConvert = useCallback(async () => {
        if (!validate())
            return;
        setConverterState('converting');
        try {
            const keys = files.map((f) => f.awsKey);
            const exportTypeNames = Array.from(selectedExportTypes);
            await fetch(`${apiBaseUrl}/captionconverter/captionconversionattempt`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'include',
                body: new URLSearchParams({
                    email,
                    ...keys.reduce((acc, key, i) => ({ ...acc, [`keys[${i}]`]: key }), {}),
                    ...exportTypeNames.reduce((acc, type, i) => ({ ...acc, [`exportTypes[${i}]`]: type }), {}),
                }),
            });
            // Track analytics
            if (typeof window !== 'undefined') {
                window.analytics?.track('Button Click', {
                    altEventName: 'CaptionConverter: Convert',
                    stateChange: 'Redirectless page change',
                    locationUrl: window.location.href,
                    buttonText: 'Convert',
                    buttonLocation: 'Main Page (after file uploaded)',
                });
            }
            // Simulate progress delay then show success
            setTimeout(() => {
                setConverterState('success');
            }, 2000);
        }
        catch (error) {
            console.error('Conversion error:', error);
            setErrors(['An error occurred during conversion. Please try again.']);
            setConverterState('wizard');
        }
    }, [validate, files, selectedExportTypes, email, apiBaseUrl]);
    /**
     * Reset to start a new conversion
     */
    const handleConvertAnother = useCallback(() => {
        setFiles([]);
        setSelectedExportTypes(new Set());
        setEmail('');
        setErrors([]);
        setConverterState('wizard');
        // Track analytics
        if (typeof window !== 'undefined') {
            window.analytics?.track('Button Click', {
                altEventName: 'CaptionConverter: Convert Again',
                stateChange: 'Redirectless page change',
                locationUrl: window.location.href,
                buttonText: 'Convert another file',
                buttonLocation: 'Main Page (after file converted)',
            });
        }
    }, []);
    // Render converting state
    if (converterState === 'converting') {
        return (_jsx("div", { className: "caption-converter-overlay", children: _jsxs("div", { className: "overlay-container", children: [_jsx("div", { className: "overlay-header", children: _jsx("span", { className: "overlay-text", children: "Caption conversion in progress..." }) }), _jsx("div", { className: "progress-bars", children: _jsx("div", { className: "progress white", children: _jsx("div", { className: "progress green animated" }) }) })] }) }));
    }
    // Render success state
    if (converterState === 'success') {
        return (_jsxs("div", { className: "caption-converter-success", children: [_jsxs("div", { className: "confirmation-page", children: [_jsx("div", { className: "confirmation-message", children: "File conversion successful!" }), _jsx("div", { className: "final-file-container", children: files.map((file) => (_jsxs("div", { className: "final-file", children: [_jsx("span", { className: "file-checkmark", children: "\u2713" }), _jsx("div", { className: "file-name", children: file.name })] }, file.id))) }), _jsxs("div", { className: "thank-you-message", children: ["Your files will be emailed to ", _jsx("span", { className: "email-highlight", children: email }), " when they're ready."] }), _jsx("button", { className: "caption-converter-button convert-again", onClick: handleConvertAnother, "data-testid": "convert-again-button", children: _jsx("span", { className: "text", children: "Convert another file" }) })] }), _jsxs("div", { className: "confirmation-container", children: [_jsx("div", { children: _jsx("span", { className: "opening-text", children: "Need quality captions or foreign subtitles?" }) }), _jsxs("div", { className: "service-links", children: [_jsx("a", { href: "/services/closed-captions", className: "service-link", "data-testid": "get-captions-link", children: "Get Captions" }), _jsx("a", { href: "/services/global-subtitles", className: "service-link", "data-testid": "get-subtitles-link", children: "Get Subtitles" })] })] })] }));
    }
    // Render wizard state
    return (_jsxs("div", { className: "caption-converter-wizard", children: [_jsxs("div", { className: "steps-bar", children: [_jsxs("div", { className: `step ${currentStep >= 1 ? 'active' : ''} ${connectorClass(1)}`, children: [_jsx("div", { className: "step-circle", children: "1" }), _jsx("div", { className: "step-label", children: "Select Caption Files" })] }), _jsxs("div", { className: `step ${currentStep >= 2 ? 'active' : ''} ${connectorClass(2)}`, children: [_jsx("div", { className: "step-circle", children: "2" }), _jsx("div", { className: "step-label", children: "Select Output Formats" })] }), _jsxs("div", { className: `step ${currentStep >= 3 ? 'active' : ''}`, children: [_jsx("div", { className: "step-circle", children: "3" }), _jsx("div", { className: "step-label", children: "Convert Caption Files" })] })] }), _jsxs("div", { className: "wizard-content", children: [_jsxs("div", { className: "content-column", children: [files.length > 0 && (_jsx("div", { className: "file-list", children: files.map((file) => (_jsxs("div", { className: `file-container ${file.isValid === false ? 'srt-error' : ''}`, children: [_jsx("span", { className: "text", children: file.isValid === false && file.errorMessage
                                                ? file.errorMessage
                                                : file.name }), _jsx("button", { className: "remove-file-button", onClick: () => handleRemoveFile(file.id), "aria-label": `Remove ${file.name}`, "data-testid": `remove-file-${file.id}`, children: "\u00D7" })] }, file.id))) })), _jsx("button", { className: "caption-converter-button select", onClick: handleUploadClick, "data-testid": "upload-srt-button", children: _jsx("span", { className: "text", children: "Select .srt Files" }) }), _jsx("div", { className: "select-hint", children: "You can select multiple files." })] }), _jsx("div", { className: `content-column center ${currentStep === 1 ? 'inactive' : ''}`, children: _jsx("ul", { className: "export-types-list", children: exportTypes.map((type) => (_jsx("li", { children: _jsxs("label", { className: "checkbox-label", children: [_jsx("input", { type: "checkbox", checked: selectedExportTypes.has(type.name), onChange: () => handleToggleExportType(type.name), disabled: currentStep === 1, "data-testid": `export-type-${type.name}` }), _jsxs("span", { children: [type.description, " (", type.extension, ")"] })] }) }, type.name))) }) }), _jsxs("div", { className: `content-column ${currentStep !== 3 ? 'inactive' : ''}`, children: [errors.length > 0 && (_jsx("div", { className: "error-list", children: errors.map((error, index) => (_jsx("div", { className: "error-message-container", children: _jsx("span", { className: "error-message", children: error }) }, index))) })), _jsx("div", { className: "email-message", children: "Enter your email to receive the converted caption files" }), _jsx("input", { type: "email", placeholder: "Email address", className: "email-input", value: email, onChange: (e) => setEmail(e.target.value), disabled: currentStep !== 3, "data-testid": "email-input" }), _jsx("button", { className: "caption-converter-button convert", onClick: handleConvert, disabled: currentStep !== 3 || !allFilesValidated, "data-testid": "convert-button", children: _jsx("span", { className: "text", children: "Convert Files" }) }), _jsxs("div", { className: "privacy-message", children: ["We respect your", ' ', _jsx("a", { href: "/about/privacy", className: "privacy-link", children: "privacy" }), ' ', "and will not share your information with 3rd parties."] })] })] })] }));
}
